/* Queue player for ruqyah recordings and duʿāʾ audio — plays a list back to
   back ("play all"), with lock-screen controls. Separate from the Qurʾān player, whose
   model is surahs and āyāt; the two hand over so only one ever plays:
   starting ruqyah stops the Qurʾān player (which leaves a resume point), and
   starting the Qurʾān player stops ruqyah. Listening time counts as Qurʾān
   listening. */
import { create } from 'zustand';
import { usePlayer } from '@/features/player/audioEngine';
import { useProgress } from '@/lib/store';
/** Anything the queue can play (ruqyah recordings, duʿāʾ). seconds 0 = unknown until loaded. */
export interface QueueTrack { id: string; title: string; reciter: string; url: string; seconds: number }

type Status = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

interface RuqyahState {
  queue: QueueTrack[];
  /** Shown on the lock screen and the now-playing bar, e.g. "Ruqyah". */
  album: string;
  index: number;
  status: Status;
  positionMs: number;
  durationMs: number;
  error: string | null;
  playQueue: (queue: QueueTrack[], index?: number, album?: string) => void;
  toggle: () => void;
  next: () => void;
  prev: () => void;
  seekMs: (ms: number) => void;
  stop: () => void;
}

let audio: HTMLAudioElement | null = null;
let listenFrom = 0;

function commitListening() {
  if (!listenFrom) return;
  const seconds = Math.round((Date.now() - listenFrom) / 1000);
  listenFrom = 0;
  if (seconds >= 5) useProgress.getState().addSession({ kind: 'listen', start: Date.now() - seconds * 1000, seconds });
}

const el = () => {
  if (audio) return audio;
  audio = new Audio();
  audio.preload = 'auto';
  audio.addEventListener('playing', () => { listenFrom ||= Date.now(); useRuqyah.setState({ status: 'playing', error: null }); });
  audio.addEventListener('pause', () => { if (audio!.ended) return; commitListening(); if (useRuqyah.getState().status === 'playing') useRuqyah.setState({ status: 'paused' }); });
  audio.addEventListener('waiting', () => { if (useRuqyah.getState().status === 'playing') useRuqyah.setState({ status: 'loading' }); });
  audio.addEventListener('timeupdate', () => {
    const ms = audio!.currentTime * 1000;
    if (Math.abs(ms - useRuqyah.getState().positionMs) > 900) useRuqyah.setState({ positionMs: ms });
    try { if (audio!.duration) navigator.mediaSession?.setPositionState({ duration: audio!.duration, position: audio!.currentTime, playbackRate: 1 }); } catch {}
  });
  audio.addEventListener('loadedmetadata', () => useRuqyah.setState({ durationMs: (audio!.duration || 0) * 1000 }));
  audio.addEventListener('ended', () => {
    commitListening();
    const st = useRuqyah.getState();
    if (st.index + 1 < st.queue.length) load(st.index + 1); // play all: on to the next recording
    else useRuqyah.setState({ status: 'paused', positionMs: 0 });
  });
  // Clearing the source on stop fires an 'error' too — only a real load failure counts.
  audio.addEventListener('error', () => { if (useRuqyah.getState().status !== 'idle' && audio!.getAttribute('src')) useRuqyah.setState({ status: 'error', error: 'Could not load this recording — check your connection.' }); });
  return audio;
};

function mediaSession(t: QueueTrack, album: string) {
  if (!('mediaSession' in navigator)) return;
  navigator.mediaSession.metadata = new MediaMetadata({
    title: t.title,
    artist: t.reciter,
    album: `${album} — For The Ummah`,
    artwork: [
      { src: `${location.origin}/icons/artwork-512.png`, sizes: '512x512', type: 'image/png' },
      { src: `${location.origin}/icons/artwork-1024.png`, sizes: '1024x1024', type: 'image/png' },
    ],
  });
  const r = useRuqyah.getState;
  navigator.mediaSession.setActionHandler('play', () => r().toggle());
  navigator.mediaSession.setActionHandler('pause', () => r().toggle());
  navigator.mediaSession.setActionHandler('nexttrack', () => r().next());
  navigator.mediaSession.setActionHandler('previoustrack', () => r().prev());
  try { navigator.mediaSession.setActionHandler('seekto', (d) => { if (d.seekTime != null) el().currentTime = d.seekTime; }); } catch {}
}

function load(index: number) {
  const st = useRuqyah.getState();
  const t = st.queue[index];
  if (!t) return;
  const a = el();
  useRuqyah.setState({ index, status: 'loading', positionMs: 0, durationMs: t.seconds * 1000, error: null });
  a.src = t.url;
  mediaSession(t, st.album);
  // A play() interrupted by stop() or by the next track rejects — only the current, still-active track may react.
  void a.play().catch(() => { const now = useRuqyah.getState(); if (now.status !== 'idle' && now.queue[now.index]?.id === t.id) useRuqyah.setState({ status: 'paused' }); });
}

export const useRuqyah = create<RuqyahState>()((set, get) => ({
  queue: [],
  album: 'Ruqyah',
  index: 0,
  status: 'idle',
  positionMs: 0,
  durationMs: 0,
  error: null,

  playQueue: (queue, index = 0, album = 'Ruqyah') => {
    // Hand over: only one player at a time.
    if (usePlayer.getState().status !== 'idle') usePlayer.getState().stop();
    set({ queue, album });
    load(index); // synchronous inside the tap, so iOS allows playback
  },
  toggle: () => {
    const a = el();
    if (get().status === 'playing' || get().status === 'loading') a.pause();
    else {
      if (usePlayer.getState().status !== 'idle') usePlayer.getState().stop();
      const t = get().queue[get().index];
      if (t) mediaSession(t, get().album); // re-claim the lock-screen controls
      void a.play().catch(() => {});
    }
  },
  next: () => { const { index, queue } = get(); if (index + 1 < queue.length) load(index + 1); },
  prev: () => {
    const { index } = get();
    // Like a music player: back to the start first, then the previous recording.
    if (el().currentTime > 5 || index === 0) el().currentTime = 0; else load(index - 1);
  },
  seekMs: (ms) => { el().currentTime = ms / 1000; set({ positionMs: ms }); },
  stop: () => {
    if (audio) { audio.pause(); audio.removeAttribute('src'); audio.load(); }
    commitListening();
    set({ status: 'idle', queue: [], index: 0, positionMs: 0 });
    if ('mediaSession' in navigator) navigator.mediaSession.metadata = null;
  },
}));

/* The Qurʾān player starting (anything other than idle) stops ruqyah. */
let watching = false;
export function watchPlayerHandover() {
  if (watching) return;
  watching = true;
  usePlayer.subscribe((p, prev) => {
    if (p.status === 'loading' && prev.status !== 'loading' && useRuqyah.getState().status !== 'idle') useRuqyah.getState().stop();
  });
}

if (import.meta.env.DEV && typeof window !== 'undefined') (window as unknown as { __ruqyah: typeof useRuqyah }).__ruqyah = useRuqyah;
